<script>
    import { onMount } from "svelte";

    import Select from "svelte-select/Select.svelte";

    import { isMobile } from "../../stores";

    import { fly } from "svelte/transition";

    import {
        SearchInput,
        SearchSelect,
        PageLoading,
        Button,
        MultiSearchSelect,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
    } from "../../Components/Common/index.svelte";

    import PatientEncounterBulkActionsModal from "../PatientEncounter/components/PatientEncounterBulkActionsModal.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { InsuranceType, QuoteStatuses } from "../../Utils/utilEnums.svelte";

    import {
        goToURL,
        replaceURL,
        getText,
        getTitle,
        getFormatDateAndTime,
        showError,
        isObjectEmpty,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        getQuoteStatus,
        useTableScrollMemory,
    } from "../../Utils/utilFunctions.svelte";

    import { cacheInsuranceFunds } from "../../Utils/utilCache.svelte";
    import { user, insuranceFund } from "../../stores";
    import { duplicateIcon, editIcon } from "../../Utils/utilConstants.svelte";
    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import SurgeonSelector from "../../Components/Common/SurgeonSelector.svelte";
    import CustomizeTable from "../../Components/Custom/Table/CustomizeTable.svelte";
    import Checkbox from "../../Components/Custom/Table/Components/EncounterTable/Checkbox.svelte";
    import BasicInfo from "../../Components/Custom/Table/Components/EncounterTable/BasicInfo.svelte";
    import InsuranceValidationInfo from "../../Components/Custom/Table/Components/EncounterTable/InsuranceValidationInfo.svelte";
    import DoctorInfo from "../../Components/Custom/Table/Components/EncounterTable/DoctorInfo.svelte";
    import ScheduledDateInfo from "../../Components/Custom/Table/Components/EncounterTable/ScheduledDateInfo.svelte";
    import TableActions from "../../Components/Custom/Table/Components/EncounterTable/TableActions.svelte";
    import BillingStatusInfo from "../../Components/Custom/Table/Components/EncounterTable/BillingStatusInfo.svelte";
    import DataId from "../../Components/Custom/Table/Components/EncounterTable/DataId.svelte";
    import HospitalInfo from "../../Components/Custom/Table/Components/EncounterTable/HospitalInfo.svelte";
    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

    export let params = {};

    let isGettingPatientQuotes = false,
        isLoadingHealthFund = false,
        isLoadingAccountDoctors = false,
        returnPatientQuoteArr = [],
        accountDoctorsArr = [],
        filters = { status: Number(0), badDebtor: Number(-1) },
        page = 0,
        pageSize = 25,
        count,
        checkboxString = "",
        selectedPatientQuotes = [],
        selectedPatientQuotesIdArr = [],
        filterSurgeonObj,
        showBulkActionsModal = false,
        bulkActionType = "",
        currentDoctorId,
        bulkChangeSurgeonButtonToolTip = "",
        showFilters = false;

    let searchInsuranceTypeArr = [],
        searchPrivateHealthFundArr = [],
        healthFundOptionsArr = [],
        isShowPrivateHealthFundSelect = false;

    let isSearchingSurgeon = false,
        searchAccountDoctorsArr = [],
        selectedAccountDoctors = [];

    let searchBillingStatusArr = [0, 1];

    let selectedDoctors = [];

    $: isUserDoctor = $user.role == 2;

    $: isUserDoctorOrPatient = $user.role == 1 || isUserDoctor;

    $: isUserEnableSeeAllFilters = $user.role >= 2;

    $: isUserEnableCreateAndEdit = $user.role > 1 && $user.role != 90;

    $: isUserAdmin = $user.role == 100;

    $: showBatchButtons = selectedPatientQuotes.length > 0;

    $: canBatchChangeSurgeon = checkIfSelectedEncountersBelongsToSameDoctor(
        selectedPatientQuotes,
        selectedPatientQuotesIdArr,
    );

    $: columns = [
        {
            key: "selected?",
            title: "",
            headerClass: "toggleSelectAllHeader text-center",
            renderComponent: {
                component: Checkbox,
            },
            stickyLeft: true,
            csvExportHidden: true,
        },
        {
            key: "patient",
            title: "Basic Info",
            headerClass: "text-left pl-4 sortable-header-decoration",
            class: "py-1.5 cursor-pointer w-[300px]",
            renderComponent: {
                component: BasicInfo,
                props: {
                    isFeeEstimate: true,
                },
            },
            csvExportHidden: true,
            stickyLeft: true,
        },
        {
            key: "id",
            title: "Est. ID",
            headerClass:
                "text-left pl-4 cursor-pointer sortable-header-decoration min-w-[100px]",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            csvContent: (v) => getText(v.id),
            renderComponent: {
                component: DataId,
            },
        },
        {
            key: "patient",
            title: "Patient",
            csvContent: (v) => _getName(v.patient),
            isHidden: true,
        },
        {
            key: "doctor",
            title: "Doctor",
            headerClass: "text-left pl-4 sortable-header-decoration",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => _getDoctor(v.doctor),
            renderComponent: {
                component: DoctorInfo,
            },
        },
        {
            key: "hospital",
            title: "Hospital",
            headerClass: "text-left pl-4 cursor-default",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            csvContent: (v) => _getHospital(v.hospital, true),
            renderComponent: {
                component: HospitalInfo,
            },
        },
        {
            key: "scheduledDate",
            title: "Scheduled Date",
            headerClass: "text-left pl-4 sortable-header-decoration",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => _getScheduleDate(v.scheduledDate),
            renderComponent: {
                component: ScheduledDateInfo,
            },
        },
        {
            key: "insuranceValidation",
            title: "Insurance",
            headerClass: "text-left pl-4 cursor-default",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            renderComponent: {
                component: InsuranceValidationInfo,
            },
            csvExportHidden: true,
        },
        {
            key: "billingStatus",
            title: "Est. Status",
            headerClass: "text-left pl-4 cursor-default",
            class: (v) => _getRowBgColor(v.checked),
            renderComponent: {
                component: BillingStatusInfo,
            },
            csvContent: (v) => getQuoteStatus(v.billingStatus),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-4 cursor-default rounded-tr-xl",
            class: (v) => "cursor-pointer" + _getRowBgColor(v.checked),
            renderComponent: {
                component: TableActions,
                props: {
                    isFeeEstimate: true,
                    saveTableScroll: () => saveTableScroll(),
                    afterModifyNotes: async () => await reload(),
                },
            },
            csvExportHidden: true,
        },
    ];

    const sortableHeaderKeys = ["id", "scheduledDate", "patient", "doctor"];

    const restrictedInsuranceTypes = InsuranceType.filter((it) => it.id < 16);

    $: _getCheckboxString(selectedPatientQuotesIdArr);

    $: if (
        selectedPatientQuotes.length >= 0 &&
        returnPatientQuoteArr.length > 0
    ) {
        selectedPatientQuotesIdArr = selectedPatientQuotes.map(
            (patient) => patient.id,
        );
        _updateHeaderIcon();
    }

    $: isLoading =
        isGettingPatientQuotes ||
        isLoadingHealthFund ||
        isLoadingAccountDoctors;

    let scrollTableContainerRef;
    const { saveScroll, restoreScrollPosition } =
        useTableScrollMemory("patientQuoteTable");

    onMount(async () => {
        if (params.query) await updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        await loadPatientQuotes(page * pageSize, pageSize, _getSearchParams());

        if (params.query) updateCheckboxByQuery(params.query);

        // In order to get health func properly
        await loadInsuranceFund();

        restoreTableScroll();
    });

    const redirectToCreatePage = () => {
        saveTableScroll();
        goToURL(`/PatientQuote/create/new`);
    };

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            if (isUserDoctorOrPatient) {
                if (isUserDoctor) await loadAccountDoctors();

                for (let i = 0; i < arr.length; i++) {
                    if (arr[i].includes("page="))
                        page = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("size="))
                        pageSize = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("surgeonId=") && isUserDoctor) {
                        filters.surgeonId = Number(arr[i].split("=")[1]);

                        filterSurgeonObj = accountDoctorsArr.filter(
                            (surgeon) => surgeon.id == filters.surgeonId,
                        )[0];
                    }

                    if (arr[i].includes("fromDate="))
                        filters.dateFrom = arr[i].split("=")[1];
                    if (arr[i].includes("patientId="))
                        filters.patientId = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("toDate="))
                        filters.dateTo = arr[i].split("=")[1];

                    if (arr[i].includes("badDebtor="))
                        filters.badDebtor = Number(arr[i].split("=")[1]);
                    else filters.badDebtor = Number(-1);
                }
            }

            if (isUserEnableSeeAllFilters) {
                for (let i = 0; i < arr.length; i++) {
                    if (arr[i].includes("page="))
                        page = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("size="))
                        pageSize = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("sort="))
                        filters.sort = arr[i].split("=")[1];

                    if (arr[i].includes("search="))
                        filters.search = arr[i].split("=")[1].toString();

                    if (arr[i].includes("encounterId="))
                        filters.encounterId = arr[i].split("=")[1].toString();

                    if (arr[i].includes("patientId="))
                        filters.patientId = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("status="))
                        filters.status = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("doctorId=")) {
                        if (isNaN(arr[i].split("=")[1])) {
                            filters.doctorId = arr[i].split("=")[1];
                            selectedDoctors = filters.doctorId
                                .split(",")
                                .map((id) => Number(id));
                        } else {
                            filters.doctorId = Number(arr[i].split("=")[1]);
                            selectedDoctors = [Number(filters.doctorId)];
                        }
                    }

                    if (arr[i].includes("quoteEncounterId="))
                        filters.quoteEncounterId = arr[i].split("=")[1];

                    if (arr[i].includes("fromDate="))
                        filters.dateFrom = arr[i].split("=")[1];

                    if (arr[i].includes("toDate="))
                        filters.dateTo = arr[i].split("=")[1];

                    if (arr[i].includes("billingStatus=")) {
                        searchBillingStatusArr = arr[i]
                            .split("=")[1]
                            .split(",");

                        handleBillingStatusChange(searchBillingStatusArr, {
                            requireReloadFunction: false,
                        });
                    }

                    if (arr[i].includes("patientInsuranceType=")) {
                        searchInsuranceTypeArr = arr[i]
                            .split("=")[1]
                            .split(",");

                        handleInsuranceTypeChange(searchInsuranceTypeArr, {
                            requireReloadFunction: false,
                        });

                        if (searchInsuranceTypeArr.includes("0"))
                            isShowPrivateHealthFundSelect = true;
                    }

                    if (arr[i].includes("patientInsuranceID="))
                        searchPrivateHealthFundArr = arr[i]
                            .split("=")[1]
                            .split(",");

                    if (arr[i].includes("surgeonIDs="))
                        selectedAccountDoctors = arr[i]
                            .split("=")[1]
                            .split(",");

                    if (arr[i].includes("commsDisabled=1"))
                        filters.commsDisabled = true;

                    if (arr[i].includes("badDebtor="))
                        filters.badDebtor = Number(arr[i].split("=")[1]);
                    else filters.badDebtor = Number(-1);
                }
            }

            updateURL();
        }
    };

    const updateCheckboxByQuery = (query) => {
        const arr = query.split("&");
        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("check=")) {
                    const checkArr = arr[i].split("=")[1].split(",");
                    checkArr.map((ca) => {
                        if (
                            !selectedPatientQuotes.some(
                                (pe) => pe.id == Number(ca),
                            )
                        ) {
                            let newPatientEncounter = { id: Number(ca) };
                            selectedPatientQuotes = [
                                ...selectedPatientQuotes,
                                newPatientEncounter,
                            ];
                            const checkbox = document.getElementById(
                                `selectCheckBoxId${ca}`,
                            );
                            if (checkbox) checkbox.checked = true;
                        }
                    });
                }
            }
        }
    };

    const loadInsuranceFund = async () => {
        isLoadingHealthFund = true;
        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheInsuranceFunds();

        _updateInsuranceFundsOptions();

        isLoadingHealthFund = false;
    };

    const loadAccountDoctors = async () => {
        isLoadingAccountDoctors = true;

        accountDoctorsArr = [];

        await apiMethods.getMethod
            .getAccountDoctors(0, 999999, {
                "filter[doctorId]": $user.id,
            })
            .then((res) => {
                if (res.results && res.results.length) {
                    res.results.map((accDoctor) => {
                        const { title, firstName, lastName, speciality } =
                            accDoctor;

                        accountDoctorsArr.push({
                            ...accDoctor,
                            displayName: `${getTitle(title)} ${firstName} ${lastName} (${speciality})`,
                        });
                    });
                }
            });

        isLoadingAccountDoctors = false;
    };

    const loadPatientQuotes = async (start, limit, params) => {
        isGettingPatientQuotes = true;
        if (params.sort) params.sort = params.sort.toLowerCase();
        if (params["filter[id]"]) {
            params = {
                "filter[id]": params["filter[id]"],
            };
        }

        await apiMethods.getMethod
            .getPatientQuote(start, limit, params)
            .then((res) => _handlePatientQuotes(res))
            .then(() => _updateHeaderSortIcon());
    };

    const handleFilterSelect = () => {
        page = 0;
        selectedPatientQuotes = [];

        updateURL();

        loadPatientQuotes(0, pageSize, _getSearchParams());
    };

    const handleBillingStatusChange = (
        searchResult = [],
        { requireReloadFunction = true },
    ) => {
        searchBillingStatusArr = [...searchResult];

        requireReloadFunction && handleFilterSelect();
    };

    const handleInsuranceTypeChange = (
        searchResult = [],
        { requireReloadFunction = true },
    ) => {
        searchInsuranceTypeArr = [...searchResult];

        if (searchInsuranceTypeArr.length && searchInsuranceTypeArr.includes(0))
            isShowPrivateHealthFundSelect = true;
        else {
            isShowPrivateHealthFundSelect = false;
            searchPrivateHealthFundArr = [];
        }

        requireReloadFunction && handleFilterSelect();
    };

    const handleHealthFundTypeChange = (searchResult = []) => {
        searchPrivateHealthFundArr = [...searchResult];

        handleFilterSelect();
    };

    const generateSelectAll = () => {
        const selectAllHeader = document.getElementsByClassName(
            "toggleSelectAllHeader",
        )[0];

        const tag = document.createElement("input");
        tag.type = "checkbox";
        tag.className =
            "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white ml-3 mt-2";
        tag.id = "toggleSelectAll";
        tag.onclick = () => {
            toggleSelectAll();
        };

        if (selectAllHeader) selectAllHeader.appendChild(tag);
    };

    const toggleSelectAll = () => {
        const selectAllCheckBox = document.getElementById("toggleSelectAll");
        if (selectAllCheckBox.className.includes("selectedAll")) {
            selectedPatientQuotes = [];
            _updateCheckBox();
        } else {
            returnPatientQuoteArr.forEach((encounter) => {
                if (!selectedPatientQuotesIdArr.includes(encounter.id)) {
                    _addSelectedPatientEncounter(encounter);
                }
            });
        }
    };

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "selected?" && className.includes("checkbox"))
            _addSelectedPatientEncounter(rowData);
        else if (
            key !== "selected?" &&
            key !== "buttons" &&
            key !== "billingStatus" &&
            rowData.id
        ) {
            saveTableScroll();
            goToURL(`/PatientQuote/view/${rowData.id}`);
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (key == "selected?") _updateCheckBox();
        else if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadPatientQuotes(0, pageSize, _getSearchParams());
        }
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadPatientQuotes(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
        _updateCheckBox();
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadPatientQuotes(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        if (checkboxString)
            replaceURL(
                `/PatientQuote/page=${page}&size=${pageSize}&check=${checkboxString}${_getParamsString()}`,
            );
        else
            replaceURL(
                `/PatientQuote/page=${page}&size=${pageSize}${_getParamsString()}`,
            );
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleSelectSurgeon = (e) => {
        filters.surgeonId = e.detail.id;

        handleFilterSelect();
    };

    const clearCurrentSurgeon = () => {
        filterSurgeonObj = null;

        if (filters.surgeonId) {
            delete filters.surgeonId;

            handleFilterSelect();
        }
    };

    const handleSelectSurgeonFromSearch = (searchResult = []) => {
        selectedAccountDoctors = [...searchResult];

        handleFilterSelect();
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => fly(node, args);

    const reload = async () => {
        saveTableScroll();
        selectedPatientQuotes = [];
        await loadPatientQuotes(page * pageSize, pageSize, _getSearchParams());
        restoreTableScroll();
    };

    const checkIfSelectedEncountersBelongsToSameDoctor = (arr, idArr) => {
        if (arr.length) {
            // loaded selection from URL, no doctor id, can't verify
            if (!arr.every((a) => a.doctorId)) {
                bulkChangeSurgeonButtonToolTip =
                    "Unable to set surgeon - encounters from multiple doctors selected";
                return false;
            }

            const firstDocID = arr[0].doctorId;
            return arr.every((i) => i.doctorId == firstDocID);
        }

        bulkChangeSurgeonButtonToolTip =
            "Unable to set surgeon - encounters from multiple doctors selected";
        return false;
    };

    const bulkArchive = () => {
        bulkActionType = "archive";
        showBulkActionsModal = true;
    };

    const bulkChangeDate = () => {
        bulkActionType = "changeDate";
        showBulkActionsModal = true;
    };

    const bulkChangeSurgeon = () => {
        bulkActionType = "changeSurgeon";
        currentDoctorId = selectedPatientQuotes[0].doctorId;
        showBulkActionsModal = true;
    };

    const bulkChangeHospital = () => {
        bulkActionType = "changeHospital";
        showBulkActionsModal = true;
    };

    const bulkChangeDoctor = () => {
        bulkActionType = "changeEncounterDoctor";
        showBulkActionsModal = true;
    };

    const showBulkActions = () => {
        if (document.getElementById("bulkActionsContainer"))
            document.getElementById("bulkActionsContainer").style.display =
                "flex";
    };

    const hideBulkActions = () => {
        if (document.getElementById("bulkActionsContainer"))
            document.getElementById("bulkActionsContainer").style.display =
                "none";
    };

    // HELPER FUNCTIONS
    const _handlePatientQuotes = (res) => {
        isGettingPatientQuotes = false;
        if (res.error) showError(res.error);
        else {
            returnPatientQuoteArr = [...res.results];
            count = res.total;
        }
        setTimeout(() => {
            generateSelectAll();
            _updateCheckBox();
        }, 1);
    };

    const _updateHeaderIcon = () => {
        const headerIcon = document.getElementById("toggleSelectAll");
        if (!headerIcon) return;
        if (selectedPatientQuotes.length === 0) {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedNone";
            headerIcon.checked = false;
            headerIcon.style = "";
        } else if (
            returnPatientQuoteArr.every((patient) =>
                selectedPatientQuotesIdArr.includes(patient.id),
            )
        ) {
            headerIcon.style.cssText = "";
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedAll";
            headerIcon.checked = true;
        } else {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 appearance-none selectedPartial";
            headerIcon.checked = false;
            headerIcon.style.cssText =
                "border: solid #270053; border-width: 4px; padding: 4px; border-radius: 5px; background-color: white; cursor: pointer; margin: 0.5rem 0 0 0.75rem";
        }
    };

    const _addSelectedPatientEncounter = (data) => {
        if (
            selectedPatientQuotes.some((encounter) => encounter.id === data.id)
        ) {
            selectedPatientQuotes = selectedPatientQuotes.filter(
                (encounter) => encounter.id !== data.id,
            );
            _updateCheckBox();
            returnPatientQuoteArr = returnPatientQuoteArr.map((encounter) => {
                if (encounter.id == data.id) encounter.checked = false;
                return encounter;
            });
        } else {
            selectedPatientQuotes = [...selectedPatientQuotes, data];
            const checkbox = document.getElementById(
                `selectCheckBoxId${data.id}`,
            );
            if (checkbox) checkbox.checked = true;
            returnPatientQuoteArr = returnPatientQuoteArr.map((encounter) => {
                if (encounter.id == data.id) encounter.checked = true;
                return encounter;
            });
        }
    };

    const _updateCheckBox = () => {
        setTimeout(() => {
            returnPatientQuoteArr.forEach((encounter) => {
                const checkbox = document.getElementById(
                    `selectCheckBoxId${encounter.id}`,
                );
                if (checkbox) {
                    if (selectedPatientQuotesIdArr.includes(encounter.id)) {
                        document.getElementById(
                            `selectCheckBoxId${encounter.id}`,
                        ).checked = true;
                        encounter.checked = true;
                    } else {
                        document.getElementById(
                            `selectCheckBoxId${encounter.id}`,
                        ).checked = false;
                        encounter.checked = false;
                    }
                }
            });
            _updateHeaderIcon();

            returnPatientQuoteArr = [...returnPatientQuoteArr];
        }, 1);
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.sort) params.sort = filters.sort;

        if (isUserEnableSeeAllFilters) {
            if (filters.search) params.search = filters.search;

            if (filters.encounterId) params["filter[id]"] = filters.encounterId;

            if (filters.status || filters.status == Number(0))
                params["filter[status]"] = filters.status;

            if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;
        }

        if (isUserDoctor && filters.surgeonId) {
            params["filter[surgeonId]"] = filters.surgeonId;
        }

        if (filters.patientId) {
            params["filter[patientId]"] = filters.patientId;
        }

        if (filters.quoteEncounterId)
            params["filter[quoteEncounterId]"] = filters.quoteEncounterId;

        if (filters.dateFrom)
            params["filter[scheduledDateFrom]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[scheduledDateTo]"] = filters.dateTo;

        if (searchBillingStatusArr.length)
            params["filter[billingStatus]"] = searchBillingStatusArr.join(",");

        if (searchInsuranceTypeArr.length)
            params["filter[patientInsuranceType]"] =
                searchInsuranceTypeArr.join(",");

        if (searchPrivateHealthFundArr.length)
            params["filter[patientInsuranceID]"] =
                searchPrivateHealthFundArr.join(",");

        if (selectedAccountDoctors.length)
            params["filter[surgeonUniqueID]"] =
                selectedAccountDoctors.join(",");

        if (filters.commsDisabled) params["filter[disableNotifications]"] = 1;

        if (!isNaN(filters.badDebtor) && Number(filters.badDebtor) >= 0)
            params["filter[badDebtor]"] = filters.badDebtor;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (isUserEnableSeeAllFilters) {
                if (params.sort) str += `&sort=${params.sort}`;

                if (params.search) str += `&search=${params.search}`;

                if (params["filter[id]"])
                    str += `&encounterId=${params["filter[id]"]}`;

                if (
                    params["filter[status]"] ||
                    params["filter[status]"] == Number(0)
                )
                    str += `&status=${params["filter[status]"]}`;

                if (
                    (params["filter[claimStatus]"] ||
                        params["filter[claimStatus]"] == Number(0)) &&
                    typeof params["filter[claimStatus]"] == "number"
                )
                    str += `&claimStatus=${params["filter[claimStatus]"]}`;

                if (params["filter[doctorId]"])
                    str += `&doctorId=${params["filter[doctorId]"]}`;
            }

            if (params["filter[quoteEncounterId]"])
                str += `&quoteEncounterId=${params["filter[quoteEncounterId]"]}`;

            if (isUserDoctor && params["filter[surgeonId]"])
                str += `&surgeonId=${params["filter[surgeonId]"]}`;

            if (params["filter[patientId]"])
                str += `&patientId=${params["filter[patientId]"]}`;

            if (params["filter[scheduledDateFrom]"])
                str += `&fromDate=${params["filter[scheduledDateFrom]"]}`;

            if (params["filter[scheduledDateTo]"])
                str += `&toDate=${params["filter[scheduledDateTo]"]}`;

            if (params["filter[billingStatus]"])
                str += `&billingStatus=${params["filter[billingStatus]"]}`;

            if (params["filter[patientInsuranceType]"])
                str += `&patientInsuranceType=${params["filter[patientInsuranceType]"]}`;

            if (params["filter[patientInsuranceID]"])
                str += `&patientInsuranceID=${params["filter[patientInsuranceID]"]}`;

            if (params["filter[surgeonUniqueID]"])
                str += `&surgeonIDs=${params["filter[surgeonUniqueID]"]}`;

            if (params["filter[underpaid]"]) str += `&underpaid=1`;

            if (params["filter[disableNotifications]"])
                str += `&commsDisabled=1`;

            if (
                !isNaN(params["filter[badDebtor]"]) &&
                Number(params["filter[badDebtor]"]) >= 0
            )
                str += `&badDebtor=${params["filter[badDebtor]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _getCheckboxString = (arr) => {
        if (arr.length) checkboxString = arr.toString();
        else checkboxString = "";

        updateURL();
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                ["id", "scheduledDate", "patient", "doctor"];

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "id" &&
                            row.childNodes[0].data == "Est. ID") ||
                        (params.sort == "scheduledDate" &&
                            row.childNodes[0].data == "Scheduled Date") ||
                        (params.sort == "patient" &&
                            row.childNodes[0].data == "Basic Info") ||
                        (params.sort == "doctor" &&
                            row.childNodes[0].data == "Doctor")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "idDesc" &&
                            row.childNodes[0].data == "Est. ID") ||
                        (params.sort == "scheduledDateDesc" &&
                            row.childNodes[0].data == "Scheduled Date") ||
                        (params.sort == "patientDesc" &&
                            row.childNodes[0].data == "Basic Info") ||
                        (params.sort == "doctorDesc" &&
                            row.childNodes[0].data == "Doctor")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };
    const _getName = ({ firstName, lastName, title, middleName }) => {
        const name = `${firstName}${
            middleName ? " " + middleName : ""
        } ${lastName}`;

        return `${getTitle(title)} ${name}`;
    };

    const _getDoctor = ({ title, fullName }) => {
        return `${getTitle(title)} ${fullName}`;
    };

    const _getSurgeon = (surgeon) => {
        if (!surgeon || isObjectEmpty(surgeon))
            return "Missing surgeon details";

        const { title, firstName, lastName } = surgeon;

        return `${title ? getTitle(title) : ""} ${firstName ?? ""} ${
            lastName ?? ""
        }`;
    };

    const _getHospital = (hospital, isCsvContent = false) => {
        if (hospital) {
            const { name, address, suburb, state, postcode, providerNumber } =
                hospital;

            return isCsvContent
                ? `${name}${state ? `, ${state}` : ""} \r\n${address}, ${suburb}, ${state}, ${postcode} (${providerNumber})`
                : `<div class="dropdown dropdown-top dropdown-hover">
                    <p>${name}${state ? `, ${state}` : ""}</p>
                    <div class="dropdown-content text-sm menu p-2 shadow bg-base-100 rounded-box w-64">
                        ${address}, ${suburb}, ${state}, ${postcode} (${providerNumber})
                    </div>
                </div>`;
        } else return "No hospital";
    };

    const _getScheduleDate = (date) => {
        if (date) {
            const dateArr = date.split("T")[0].split("-");
            if (Number(dateArr[0]) < 1900) return "No Date";
            return getFormatDateAndTime(date);
        } else return "No Date";
    };

    const _renderOPVStatus = (medicareStatus, healthFundStatus) => {
        const successColor = "text-greenDefault";
        const failedColor = "text-red-400";
        const notCheckedColor = "text-gray-400";

        const validStatus = [1, 2, 3];

        const notCheckedStr = `<span class="${notCheckedColor}">Not checked</span>`;

        if (
            !validStatus.includes(medicareStatus) &&
            !validStatus.includes(healthFundStatus)
        )
            return notCheckedStr;

        const result = [];

        if (validStatus.includes(medicareStatus))
            result.push(
                `<span class="${
                    medicareStatus == 1 ? successColor : failedColor
                }">Medicare</span>`,
            );

        if (validStatus.includes(healthFundStatus))
            result.push(
                `<span class="${
                    healthFundStatus == 1 ? successColor : failedColor
                }">Health Fund</span>`,
            );

        return result.toString().replaceAll(",", "<br>");
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-center gap-x-2">
                        <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Patient Fee Estimate">
                            <i class="editIcon ${editIcon}"></i>
                        </div>
                        <div class="duplicateIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Duplicate Patient Fee Estimate">
                            <i class="duplicateIcon ${duplicateIcon}"></i>
                        </div>
                        <div class="convertQuoteToEncounterIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Convert Fee Estimate to Encounter">
                            <i class="convertQuoteToEncounterIcon fa fa-hand-holding-medical"></i>
                        </div>
                    </div>`;
    };

    const _updateInsuranceFundsOptions = () => {
        healthFundOptionsArr = $insuranceFund.map((fund) => ({
            key: fund.id,
            value: fund.name,
        }));
    };

    const _syncEncounterDetails = (res) => {
        return {
            ...res,
            billingStatus: 0,
            quoteOnly: Boolean(false),
            quoteEncounterId: Number(res.id),
        };
    };

    const _getRowBgColor = (isChecked = false) =>
        isChecked ? " bg-[#F3F3F3]" : "";

    const saveTableScroll = () => {
        if (scrollTableContainerRef)
            saveScroll(
                scrollTableContainerRef.scrollTop,
                scrollTableContainerRef.scrollLeft,
            );
    };

    const restoreTableScroll = () => {
        if (!scrollTableContainerRef) return;

        scrollTableContainerRef.scrollTop = restoreScrollPosition().top;
        scrollTableContainerRef.scrollLeft = restoreScrollPosition().left;

        saveScroll();
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <div class="flex flex-row justify-between">
            {#if isUserEnableSeeAllFilters}
                <button
                    class="btn btn-accent btn-sm w-32 flex flex-row gap-x-1"
                    on:click={toggleFilters}
                >
                    Filters
                    <i class="fa fa-plus mr-2 text-[14px]" aria-hidden="true" />
                    <i
                        style="font-size:14px"
                        class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                        >&#xf104;
                    </i>
                </button>
            {/if}
            <div class="flex flex-row">
                {#if isUserEnableCreateAndEdit}
                    <div
                        class="flex flex-row flex-wrap gap-2 justify-end {$isMobile
                            ? ''
                            : 'max-w-[50%]'}"
                    >
                        {#if showBatchButtons}
                            <div
                                class="flex-none ml-2"
                                on:mouseleave={hideBulkActions}
                            >
                                <ul class="menu menu-horizontal p-0">
                                    <li tabindex="0" class="justify-end">
                                        <div
                                            class="flex"
                                            on:mouseenter={showBulkActions}
                                        >
                                            <div
                                                class="flex flex-col items-end"
                                            >
                                                Actions
                                            </div>
                                            <svg
                                                class="fill-current"
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                ><path
                                                    d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
                                                /></svg
                                            >
                                        </div>
                                        <ul
                                            id="dropdownMenu"
                                            class="z-[100] overflow-hidden bg-zinc-200 border-zinc-300"
                                        >
                                            <Button
                                                manualClass="rounded-none text-[#6f726c] bg-zinc-200 leading-0 hover:bg-zinc-300"
                                                clickFunc={bulkArchive}
                                            >
                                                Archive Selection
                                            </Button>
                                            <Button
                                                manualClass="rounded-none text-[#6f726c] bg-zinc-200 leading-0 hover:bg-zinc-300"
                                                clickFunc={bulkChangeDate}
                                            >
                                                Change Date
                                            </Button>
                                            <Button
                                                manualClass="rounded-none text-[#6f726c] bg-zinc-200 leading-0 hover:bg-zinc-300"
                                                clickFunc={bulkChangeHospital}
                                            >
                                                Change Hospital
                                            </Button>
                                            <Button
                                                manualClass="rounded-none text-[#6f726c] bg-zinc-200 leading-0 hover:bg-zinc-300"
                                                clickFunc={bulkChangeSurgeon}
                                                isDisabled={!canBatchChangeSurgeon}
                                                disabledTooltip={bulkChangeSurgeonButtonToolTip}
                                            >
                                                Change Surgeon
                                            </Button>
                                            {#if isUserAdmin}
                                                <Button
                                                    manualClass="rounded-none text-[#6f726c] bg-zinc-200 leading-0 hover:bg-zinc-300"
                                                    clickFunc={bulkChangeDoctor}
                                                >
                                                    Change Doctor
                                                </Button>
                                            {/if}
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        {/if}
                    </div>
                {/if}

                {#if isUserEnableSeeAllFilters}
                    <MultiSearchSelect
                        title="Billing Status"
                        inputID="encounterBillingStatus"
                        isDisabled={isGettingPatientQuotes}
                        optionsArr={QuoteStatuses}
                        bind:searchResult={searchBillingStatusArr}
                        onChangeFunc={handleBillingStatusChange}
                    />
                {/if}
            </div>
        </div>

        {#if showFilters && isUserEnableSeeAllFilters}
            <div
                class="flex items-end w-auto items-start gap-4 flex-wrap"
                in:fly={{ duration: 200, y: -50 }}
            >
                <div class="w-full">
                    <Button clickFunc={redirectToCreatePage}>
                        Create New Fee Estimate
                    </Button>
                </div>
                <SearchInput
                    bind:value={filters.encounterId}
                    title="Fee Estimate ID"
                    inputFunc={() => {
                        if (!isNaN(filters.encounterId)) handleFilterSelect();
                    }}
                    type="number"
                    id="encounterIdInput"
                />

                <SearchInput
                    bind:value={filters.search}
                    title="Search"
                    inputFunc={handleFilterSelect}
                />

                {#if isUserDoctor}
                    <div class="flex flex-col items-start borderB">
                        <p class="text-[11px] font-[500]">Surgeon/Referrer</p>

                        <div class="autoFillSelector w-60 mt-2">
                            <Select
                                bind:value={filterSurgeonObj}
                                items={accountDoctorsArr}
                                on:clear={clearCurrentSurgeon}
                                on:select={handleSelectSurgeon}
                                itemId="id"
                                label="displayName"
                                disabled={isLoadingAccountDoctors}
                                noOptionsMessage="No Surgeon/Referrer Found"
                                --background="#E5E7EB"
                                --border="none"
                                --border-focused="none"
                                --border-hover="none"
                                --list-z-index="3"
                            />
                        </div>
                    </div>
                {:else}
                    <DefaultDoctorFilter
                        bind:filter={filters}
                        filterPropertyName={"doctorId"}
                        onChangeFunc={() => {
                            selectedDoctors = filters["doctorId"].split(",");
                            handleFilterSelect();
                        }}
                        isLong
                    />

                    <DoctorMultiSearchSelect
                        bind:searchResult={selectedDoctors}
                        onChangeFunc={handleSelectDoctor}
                        inputClass="w-32"
                        zIndex="100"
                    />
                {/if}

                <SearchSelect
                    bind:value={filters.status}
                    title="Status"
                    onChangeFunc={handleFilterSelect}
                    isDisabled={isGettingPatientQuotes}
                >
                    <option value={Number(0)} selected>Active</option>
                    <option value={Number(1)}>On Hold</option>
                    <option value={Number(5)}>Debt Collection</option>
                    <option value={Number(7)}>Pending Cancellation</option>
                    <option value={Number(8)}>Cancelled</option>
                    <option value={Number(9)}>Archived</option>
                </SearchSelect>

                <SearchInput
                    bind:value={filters.quoteEncounterId}
                    title="Created Encounter ID"
                    inputFunc={() => {
                        if (!isNaN(filters.quoteEncounterId))
                            handleFilterSelect();
                    }}
                    id="quoteEncounterId"
                />

                {#if !$user.settings?.disableDateRangeInput}
                    <SearchDateRangeInput
                        bind:startDate={filters.dateFrom}
                        bind:endDate={filters.dateTo}
                        title="Date Range"
                        inputClass="borderB w-[272px]"
                        on:change={handleFilterSelect}
                        showClearButton
                        disable={isGettingPatientQuotes}
                        showScrollYearOptions
                    />
                {:else}
                    <SearchInput
                        bind:value={filters.dateFrom}
                        title="Date from"
                        type="date"
                        isLong
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingPatientQuotes}
                        maxDate={filters.dateTo ?? undefined}
                        showScrollYearOptions
                    />

                    <SearchInput
                        bind:value={filters.dateTo}
                        title="Date to"
                        type="date"
                        isLong
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingPatientQuotes}
                        minDate={filters.dateFrom ?? undefined}
                        showScrollYearOptions
                    />
                {/if}

                <MultiSearchSelect
                    title="Insurance Type"
                    inputID="patientInsuranceType"
                    isDisabled={isGettingPatientQuotes}
                    optionsArr={restrictedInsuranceTypes}
                    bind:searchResult={searchInsuranceTypeArr}
                    onChangeFunc={handleInsuranceTypeChange}
                    zIndex="99"
                />

                {#if isShowPrivateHealthFundSelect}
                    <MultiSearchSelect
                        title="Private Health Fund"
                        inputID="patientHealthFundType"
                        isDisabled={isGettingPatientQuotes}
                        optionsArr={healthFundOptionsArr}
                        bind:searchResult={searchPrivateHealthFundArr}
                        onChangeFunc={handleHealthFundTypeChange}
                        zIndex="98"
                    />
                {/if}

                <SurgeonSelector
                    isDisabled={isGettingPatientQuotes}
                    {selectedDoctors}
                    bind:selectedSurgeonIds={selectedAccountDoctors}
                    onChangeFunc={handleSelectSurgeonFromSearch}
                    zIndex="97"
                />

                <SearchSelect
                    bind:value={filters.badDebtor}
                    title="Bad Debtor"
                    onChangeFunc={handleFilterSelect}
                    isDisabled={isGettingPatientQuotes}
                >
                    <option value={Number(-1)} selected>All</option>
                    <option value={Number(0)}>Non-Bad Debtors</option>
                    <option value={Number(1)}> Bad Debtors </option>
                </SearchSelect>

                <div class="flex flex-row gap-x-[6px]">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={filters.commsDisabled}
                        id="disabledComms"
                        on:change={handleFilterSelect}
                        disabled={isGettingPatientQuotes}
                    />
                    <label for="disabledComms">Comms Disabled</label>
                </div>
            </div>
        {/if}

        <ResetFilterLink
            bind:filter={filters}
            specialVariables={{
                searchInsuranceTypeArr,
                searchPrivateHealthFundArr,
                selectedAccountDoctors,
                searchBillingStatusArr,
            }}
            onResetFilter={() => {
                selectedAccountDoctors = [];
                searchInsuranceTypeArr = [];
                handleInsuranceTypeChange(searchInsuranceTypeArr, {
                    requireReloadFunction: false,
                });
                searchPrivateHealthFundArr = [];
                searchBillingStatusArr = [0, 1];
                handleBillingStatusChange(searchBillingStatusArr, {
                    requireReloadFunction: false,
                });

                clearFilterDoctor();
            }}
        />
    {:else}
        <div
            class="flex flex-0 items-start justify-between"
            in:filterTransition={{ duration: 200, y: -50 }}
        >
            <div class="flex flex-row items-end justify-start flex-wrap gap-4">
                {#if isUserEnableSeeAllFilters}
                    <SearchInput
                        bind:value={filters.encounterId}
                        title="Fee Estimate ID"
                        inputFunc={() => {
                            if (!isNaN(filters.encounterId))
                                handleFilterSelect();
                        }}
                        type="number"
                        id="encounterIdInput"
                    />

                    <SearchInput
                        bind:value={filters.search}
                        title="Search"
                        inputFunc={handleFilterSelect}
                    />

                    {#if isUserDoctor}
                        <div class="flex flex-col items-start borderB">
                            <p class="text-[11px] font-[500]">
                                Surgeon/Referrer
                            </p>

                            <div class="autoFillSelector w-60 mt-2">
                                <Select
                                    bind:value={filterSurgeonObj}
                                    items={accountDoctorsArr}
                                    on:clear={clearCurrentSurgeon}
                                    on:select={handleSelectSurgeon}
                                    itemId="id"
                                    label="displayName"
                                    disabled={isLoadingAccountDoctors}
                                    noOptionsMessage="No Surgeon/Referrer Found"
                                    --background="#E5E7EB"
                                    --border="none"
                                    --border-focused="none"
                                    --border-hover="none"
                                    --list-z-index="3"
                                />
                            </div>
                        </div>
                    {:else}
                        <DefaultDoctorFilter
                            bind:filter={filters}
                            filterPropertyName={"doctorId"}
                            onChangeFunc={() => {
                                selectedDoctors =
                                    filters["doctorId"].split(",");
                                handleFilterSelect();
                            }}
                        />

                        <DoctorMultiSearchSelect
                            bind:searchResult={selectedDoctors}
                            onChangeFunc={handleSelectDoctor}
                            inputClass="w-32"
                            zIndex="100"
                        />
                    {/if}
                    <SearchSelect
                        bind:value={filters.status}
                        title="Status"
                        onChangeFunc={handleFilterSelect}
                        isDisabled={isGettingPatientQuotes}
                    >
                        <option value={Number(0)} selected>Active</option>
                        <option value={Number(1)}>On Hold</option>
                        <option value={Number(5)}>Debt Collection</option>
                        <option value={Number(7)}>Pending Cancellation</option>
                        <option value={Number(8)}>Cancelled</option>
                        <option value={Number(9)}>Archived</option>
                    </SearchSelect>

                    <MultiSearchSelect
                        title="Billing Status"
                        inputID="encounterBillingStatus"
                        isDisabled={isGettingPatientQuotes}
                        optionsArr={QuoteStatuses}
                        bind:searchResult={searchBillingStatusArr}
                        onChangeFunc={handleBillingStatusChange}
                        inputClass="w-32"
                    />

                    <SearchInput
                        bind:value={filters.quoteEncounterId}
                        title="Created Encounter ID"
                        inputFunc={() => {
                            if (!isNaN(filters.quoteEncounterId))
                                handleFilterSelect();
                        }}
                        id="quoteEncounterId"
                    />

                    {#if !$user.settings?.disableDateRangeInput}
                        <SearchDateRangeInput
                            bind:startDate={filters.dateFrom}
                            bind:endDate={filters.dateTo}
                            title="Date Range"
                            inputClass="borderB w-[272px]"
                            on:change={handleFilterSelect}
                            showClearButton
                            disable={isGettingPatientQuotes}
                            showScrollYearOptions
                        />
                    {:else}
                        <SearchInput
                            bind:value={filters.dateFrom}
                            title="Date from"
                            type="date"
                            inputClass="borderB"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingPatientQuotes}
                            maxDate={filters.dateTo ?? undefined}
                            showScrollYearOptions
                        />

                        <SearchInput
                            bind:value={filters.dateTo}
                            title="Date to"
                            type="date"
                            inputClass="borderB"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingPatientQuotes}
                            minDate={filters.dateFrom ?? undefined}
                            showScrollYearOptions
                        />
                    {/if}

                    <MultiSearchSelect
                        title="Insurance Type"
                        inputID="patientInsuranceType"
                        isDisabled={isGettingPatientQuotes}
                        optionsArr={restrictedInsuranceTypes}
                        bind:searchResult={searchInsuranceTypeArr}
                        onChangeFunc={handleInsuranceTypeChange}
                        inputClass="w-32"
                        zIndex="99"
                    />

                    {#if isShowPrivateHealthFundSelect}
                        <MultiSearchSelect
                            title="Private Health Fund"
                            inputID="patientHealthFundType"
                            isDisabled={isGettingPatientQuotes}
                            optionsArr={healthFundOptionsArr}
                            bind:searchResult={searchPrivateHealthFundArr}
                            onChangeFunc={handleHealthFundTypeChange}
                            inputClass="w-32"
                            zIndex="98"
                        />
                    {/if}

                    <SurgeonSelector
                        isDisabled={isGettingPatientQuotes}
                        {selectedDoctors}
                        bind:selectedSurgeonIds={selectedAccountDoctors}
                        onChangeFunc={handleSelectSurgeonFromSearch}
                        inputClass="w-32"
                        zIndex="97"
                    />

                    <SearchSelect
                        bind:value={filters.badDebtor}
                        title="Bad Debtor"
                        onChangeFunc={handleFilterSelect}
                        isDisabled={isGettingPatientQuotes}
                    >
                        <option value={Number(-1)} selected>All</option>
                        <option value={Number(0)}>Non-Bad Debtors</option>
                        <option value={Number(1)}> Bad Debtors </option>
                    </SearchSelect>

                    <div class="flex flex-row gap-x-[6px]">
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={filters.commsDisabled}
                            id="disabledComms"
                            on:change={handleFilterSelect}
                            disabled={isGettingPatientQuotes}
                        />
                        <label for="disabledComms">Comms Disabled</label>
                    </div>

                    <ResetFilterLink
                        bind:filter={filters}
                        specialVariables={{
                            searchInsuranceTypeArr,
                            searchPrivateHealthFundArr,
                            selectedAccountDoctors,
                            searchBillingStatusArr,
                        }}
                        onResetFilter={() => {
                            selectedAccountDoctors = [];
                            searchInsuranceTypeArr = [];
                            handleInsuranceTypeChange(searchInsuranceTypeArr, {
                                requireReloadFunction: false,
                            });
                            searchPrivateHealthFundArr = [];
                            searchBillingStatusArr = [0, 1];
                            handleBillingStatusChange(searchBillingStatusArr, {
                                requireReloadFunction: false,
                            });

                            clearFilterDoctor();
                        }}
                    />
                {/if}
            </div>
            {#if isUserEnableCreateAndEdit}
                <div
                    class="flex lg:flex-row md:flex-col-reverse md:items-end flex-nowrap gap-2 justify-end'}"
                >
                    {#if showBatchButtons}
                        <div
                            class="flex-none ml-2"
                            on:mouseleave={hideBulkActions}
                        >
                            <ul class="menu menu-horizontal p-0">
                                <li tabindex="0" class="justify-end">
                                    <div
                                        class="flex"
                                        on:mouseenter={showBulkActions}
                                    >
                                        <div class="flex flex-col items-end">
                                            Actions
                                        </div>
                                        <svg
                                            class="fill-current"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            ><path
                                                d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
                                            /></svg
                                        >
                                    </div>
                                    <ul
                                        id="dropdownMenu"
                                        class="z-[100] overflow-hidden border-zinc-300"
                                    >
                                        <Button
                                            manualClass="rounded-none text-[#6f726c] bg-zinc-200 hover:bg-zinc-300"
                                            clickFunc={bulkArchive}
                                        >
                                            Archive Selection
                                        </Button>
                                        <Button
                                            manualClass="rounded-none text-[#6f726c] bg-zinc-200 hover:bg-zinc-300"
                                            clickFunc={bulkChangeDate}
                                        >
                                            Change Date
                                        </Button>
                                        <Button
                                            manualClass="rounded-none text-[#6f726c] bg-zinc-200 hover:bg-zinc-300"
                                            clickFunc={bulkChangeHospital}
                                        >
                                            Change Hospital
                                        </Button>
                                        <Button
                                            manualClass="rounded-none text-[#6f726c] bg-zinc-200 hover:bg-zinc-300"
                                            clickFunc={bulkChangeSurgeon}
                                            isDisabled={!canBatchChangeSurgeon}
                                            disabledTooltip={bulkChangeSurgeonButtonToolTip}
                                        >
                                            Change Surgeon
                                        </Button>
                                        {#if isUserAdmin}
                                            <Button
                                                manualClass="rounded-none text-[#6f726c] bg-zinc-200 hover:bg-zinc-300"
                                                clickFunc={bulkChangeDoctor}
                                            >
                                                Change Doctor
                                            </Button>
                                        {/if}
                                    </ul>
                                </li>
                            </ul>
                        </div>
                    {/if}
                    <Button
                        clickFunc={redirectToCreatePage}
                        manualClass="w-[240px]"
                    >
                        Create New Fee Estimate
                    </Button>
                </div>
            {/if}
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isLoading}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
                bind:this={scrollTableContainerRef}
            >
                <CustomizeTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnPatientQuoteArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        showSelected
        selectedCount={selectedPatientQuotes.length}
        tableData={returnPatientQuoteArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnPatientQuoteArr,
            fileName: "Fee Estimates",
            allDataApi: async () =>
                await apiMethods.getMethod.getPatientQuote(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

<PatientEncounterBulkActionsModal
    bind:isModalOpen={showBulkActionsModal}
    bind:bulkActionType
    selectedPatientEncountersIdArr={selectedPatientQuotesIdArr}
    doctorId={currentDoctorId}
    on:actionFinished={reload}
    isPatientQuote
/>
